import { useCallback, useMemo, useState } from 'react';
import { useConfigInputs, useConfigActions, useVendorCtx } from 'shared/state/configContexts';
import { toRackModels, RACK_SIZE_OPTIONS, type RackUnitModel } from 'shared/lib/rackLayout';

export function useRackView() {
  const { config } = useConfigInputs();
  const { updateConfig } = useConfigActions();
  const { productDb } = useVendorCtx();
  // R8: hold only the LABEL, not the unit object. Labels are unique across every rack
  // (`D-Node 1`, `JBOD 2`, …). Storing the object itself would snapshot it at click time;
  // after a config change rebuilds `racks`, that snapshot goes stale (or the unit may not
  // exist any more), and the inspector would show a ghost.
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  const rackSize = Number(config.rackSize ?? 42);

  /** toRackModels wraps calculateRackLayout unchanged (shared/lib/rackLayout — P3):
   *  rackSlidePPTGenerator imports the same underlying function, so its return shape
   *  is contractual. */
  const racks = useMemo(() => toRackModels(config, productDb, rackSize), [config, productDb, rackSize]);

  // Re-resolve the label against the CURRENT racks on every render, rather than trusting
  // a stored object: if the selected unit no longer exists (e.g. its node was removed),
  // this naturally falls through to null and the inspector clears itself.
  const selected = useMemo<RackUnitModel | null>(() => {
    if (selectedLabel === null) return null;
    for (const rack of racks) {
      const found = rack.units.find(({ unit }) => unit.label === selectedLabel);
      if (found) return found.unit;
    }
    return null;
  }, [racks, selectedLabel]);

  const select = useCallback((unit: RackUnitModel | null) => setSelectedLabel(unit ? unit.label : null), []);

  const onRackSizeChange = useCallback((value: string) => updateConfig({ rackSize: Number(value) }), [updateConfig]);

  /** Each add is one updateConfig so domain/rules reconcile minimums and encoding. */
  const addNode = useCallback((kind: 'd' | 'f' | 's') => {
    if (kind === 'd') { updateConfig({ veloCount: Number(config.veloCount ?? 0) + 1 }); return; }
    if (kind === 'f') { updateConfig({ afaEnabled: true, afaNodeCount: Number(config.afaNodeCount ?? 0) + 1 }); return; }
    updateConfig({ hybridEnabled: true, hybridNodeCount: Number(config.hybridNodeCount ?? 0) + 1 });
  }, [config.veloCount, config.afaNodeCount, config.hybridNodeCount, updateConfig]);

  // calculateRackLayout and RackImage both read the vendor from config.hardwareVendor
  // (RealisticRackVisualization.jsx:11; RackImage's `vendor` prop is fed from it in the
  // classic view) — NOT from useVendorCtx().vendor.id. Deriving it from the same key the
  // layout used keeps them in lockstep; reading it from useVendorCtx instead would let a
  // mid-flight vendor switch draw one vendor's photos over another vendor's layout for a
  // render or two while the config write settles (ruling P6).
  const vendorId = config.hardwareVendor || 'AIC';

  return {
    racks,
    rackSize: String(rackSize),
    rackSizeOptions: RACK_SIZE_OPTIONS,
    onRackSizeChange,
    addNode,
    selected,
    select,
    vendorId,
    isPhotographic: vendorId === 'SMC',
  };
}
